import React, { memo } from 'react';
import { getImageUrl } from '../utils/urlUtils';

/**
 * A reusable image gallery component for displaying and managing images
 * @param {Object} props Component props
 * @param {Array} props.existingImages Array of existing image URLs or paths
 * @param {Array} props.imageFiles Array of File objects for new uploads
 * @param {Function} props.onRemoveExisting Function to call when removing an existing image
 * @param {Function} props.onRemoveNew Function to call when removing a new image
 * @param {boolean} props.loading Whether images are currently loading
 * @param {string} props.baseUrl Base URL for images (defaults to REACT_APP_BASE_URL)
 * @returns {JSX.Element} Image gallery component
 */
const ImageGallery = memo(({
  existingImages = [],
  imageFiles = [],
  onRemoveExisting,
  onRemoveNew,
  loading = false,
  baseUrl = process.env.REACT_APP_BASE_URL
}) => {
  // Handle fallback for image loading errors
  const handleImageError = (e, imageUrl) => {
    // Try direct URL without any processing
    const originalPath = imageUrl.split('/').pop();
    const fallbackUrl = `${baseUrl}/uploads/${originalPath}`;
    e.target.src = fallbackUrl;
    
    // If fallback also fails, use placeholder
    e.target.onerror = () => {
      e.target.src = '/placeholder-image.jpg';
      e.target.onerror = null;
    };
  };

  return (
    <div className="space-y-4">
      {loading && (
        <div className="flex justify-center items-center h-32 bg-gray-100 rounded-lg">
          <div className="flex flex-col items-center">
            <svg className="animate-spin h-8 w-8 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm text-gray-500">Loading images...</span>
          </div>
        </div>
      )}
      
      {existingImages.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Current Images</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {existingImages.map((imageUrl, index) => (
              <div key={`existing-${index}`} className="relative group">
                <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-100">
                  <img
                    src={getImageUrl(imageUrl)}
                    alt={`Current ${index + 1}`}
                    className="h-full w-full object-cover"
                    onError={(e) => handleImageError(e, imageUrl)}
                    style={{ maxHeight: '100%', maxWidth: '100%' }}
                  />
                  {onRemoveExisting && (
                    <button
                      type="button"
                      onClick={() => onRemoveExisting(imageUrl)}
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <span className="sr-only">Remove image</span>
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 truncate">
                  {imageUrl.split('/').pop()}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {imageFiles.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">New Images to Upload</h4>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {imageFiles.map((file, index) => (
              <div key={`new-${index}`} className="relative group">
                <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-100">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={`New ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                  {onRemoveNew && (
                    <button
                      type="button"
                      onClick={() => onRemoveNew(index)}
                      className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <span className="sr-only">Remove image</span>
                      <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 truncate">
                  {file.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

ImageGallery.displayName = 'ImageGallery';

export default ImageGallery; 